import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { DescriptionInputSection, FileBox } from './styles';
import TiptapEditor from '../../tiptapEditor';
import { isMediaRecordingSupported, removeFileFromFirebase } from '../../../helpers/common';
import RecordingSnapAttach from '../../recording';
import Modal from '../../modals/modal';
import InsertGifModal from '../../modals/insertGifModal';
import { RenderFilePreview } from '../../renderFilePreview';
import { rootStore } from '../../../mobx/rootStore';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { RawFile } from '../../../mobx/interfaces/task';
interface Props {
  valueContent: string;
  setValueContent: (value: string) => void;
  setPropsLoading: (value: boolean) => void;
  disable?: boolean;
  uploadedFiles: RawFile[];
  setUploadedFiles: (files: RawFile[]) => void;
  setStateLoading: (value: boolean) => void;
  handleOpenSnapModal: () => void;
  handleFileInput: () => void;
  uploadFolder?: string;
}
const DescriptionInputBox = forwardRef((props: Props, ref) => {
  const {
    disable = false,
    uploadedFiles,
    setUploadedFiles,
    valueContent,
    setValueContent,
    setPropsLoading,
    setStateLoading,
    handleOpenSnapModal,
    handleFileInput,
    uploadFolder
  } = props;
  // Ref variables
  const editorRef = useRef<TiptapEditorRef>(null);
  //States
  const [openGifModal, setIsOpenGifModal] = useState(false);

  useImperativeHandle(ref, () => ({
    addRecordingCard(fileUrl: string, fileName: string) {
      editorRef.current?.addRecordingCard(fileUrl, fileName);
    },
    addImage(fileUrl: string) {
      editorRef.current?.addImage(fileUrl);
    },
    addImageFile(file: File[]) {
      editorRef.current?.addImageFile(file);
    },
    clearContent() {
      editorRef?.current?.clearValue();
    },
    focus() {
      editorRef?.current?.messageInputFocus();
    }
  }));

  const onClickClose = useCallback(
    (index: number, file: RawFile) => {
      if (file && !file.uploadFileType && file?.href?.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
      const filesClone = JSON.parse(JSON.stringify(uploadedFiles));
      filesClone.splice(index, 1);
      setUploadedFiles(filesClone);
    },
    [uploadedFiles, setUploadedFiles]
  );

  const addDroppedFiles = useCallback((files: RawFile[]) => {
    rootStore?.taskStore?.createTaskInput?.addFiles(files);
  }, []);

  const uploadSnapEmbed = useCallback((firebaseUrl: string, fileName: string, videoId: string) => {
    editorRef?.current?.addRecordingCard(firebaseUrl, fileName, videoId);
  }, []);

  const handleOpenGifModal = useCallback(() => {
    setIsOpenGifModal(true);
  }, []);

  const handleCloseGifModal = useCallback(() => {
    setIsOpenGifModal(false);
  }, []);

  return (
    <>
      <DescriptionInputSection>
        <TiptapEditor
          ref={editorRef}
          placeholder='Add Description....'
          valueContent={valueContent}
          setValueContent={setValueContent}
          users={[]}
          upload={{
            setLoading: (value: boolean) => {
              setPropsLoading(value);
            },
            setStateLoading: (value: boolean) => setStateLoading(value),
            addUploadedFiles: addDroppedFiles,
            uploadFolder
          }}
          toolbarActions={{
            recordSnapModalOpen: handleOpenSnapModal,
            handleFileInput,
            handleGifModal: handleOpenGifModal
          }}
          dropBox={{ isVisible: true }}
        />
        {isMediaRecordingSupported() && (
          <RecordingSnapAttach
            setLoading={(value: boolean) => {
              setPropsLoading(value);
            }}
            uploadSnapEmbed={uploadSnapEmbed}
          />
        )}
        {!disable && uploadedFiles?.length > 0 && (
          <FileBox>
            {uploadedFiles?.length > 0 &&
              uploadedFiles?.map((file: RawFile, index: number) => {
                return (
                  <>
                    <RenderFilePreview
                      key={file?.href}
                      file={file}
                      isClose={true}
                      maxWidth={210}
                      onClose={() => onClickClose(index, file)}
                    />
                  </>
                );
              })}
          </FileBox>
        )}
      </DescriptionInputSection>
      <Modal open={openGifModal} onClose={handleCloseGifModal} maxWidth='686px' isResponsiveMaxWidth={true}>
        <InsertGifModal
          setSelectedGif={(url: string) => {
            editorRef.current?.addImage(url);
          }}
          closeModal={handleCloseGifModal}
        />
      </Modal>
    </>
  );
});

export default DescriptionInputBox;
